import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "../../../icons/Search";
import Warning from "../../../icons/Warning";
import StatusBadge from "../../components/StatusBadge";
import { useViolationSummary } from "./validationState";
import { processTreeHasChanges } from "../../utils/conditions";

const Panel = styled(Paper)(({ theme }) => ({
  flex: "0 0 300px",
  display: "flex",
  flexDirection: "column",
  height: "fit-content",
  maxHeight: "calc(100vh - 200px)",
  backgroundColor: "#f8f8f8",
  borderRadius: "4px",
  overflow: "hidden",
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  paddingBottom: theme.spacing(1.5),
}));

const ScrollableContent = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  paddingLeft: "20px",
  paddingRight: "20px",
  paddingBottom: "20px",
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
  },
  marginBottom: theme.spacing(2),
}));

type TreeRowProps = {
  depth: number;
  isActive?: boolean;
  disabled?: boolean;
};

const TreeRow = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "depth" && prop !== "isActive" && prop !== "disabled",
})<TreeRowProps>(({ theme, depth, isActive, disabled }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: theme.spacing(1.2, 1.5),
  paddingLeft: theme.spacing(1.5 + depth * 3),
  cursor: disabled ? "default" : "pointer",
  backgroundColor: isActive ? "#e0e0e0" : "transparent",
  color: disabled ? "#aaa" : isActive ? "#000" : "#555",
  borderRadius: "4px",
  marginBottom: theme.spacing(0.5),
  transition: "background-color 0.2s ease",
  position: "relative",
  "&:hover": {
    backgroundColor: disabled || isActive ? "#e0e0e0" : "#ececec",
  },
  opacity: disabled ? 0.7 : 1,
}));

const DepthLineContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "depth",
})<{ depth: number }>(({ theme, depth }) => ({
  position: "relative",
}));

const DepthLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== "depth",
})<{ depth: number }>(({ theme, depth }) => {
  // Calculate opacity: starts at 0.15 for depth 1, increases by 0.1 per level, max 0.55
  const opacity = Math.min(0.15 + (depth - 1) * 0.1, 0.55);
  const color = `rgba(0, 0, 0, ${opacity})`;

  return {
    position: "absolute",
    left: theme.spacing(1.5 + (depth - 1) * 3 + 0.5),
    top: 0,
    bottom: 0,
    width: "2px",
    backgroundImage: `repeating-linear-gradient(
      to bottom,
      ${color} 0px,
      ${color} 4px,
      transparent 4px,
      transparent 8px
    )`,
    pointerEvents: "none",
    zIndex: 1,
  };
});

const TreeLabel = styled(Box)({
  flex: 1,
  fontSize: "13px",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 400,
  color: "#555",
  paddingTop: "2px",
});

const ItemTypeLabel = styled("span")({
  fontWeight: 500,
  color: "#333",
});

const ItemNameLabel = styled("span")({
  fontSize: "12px",
  color: "#777",
  marginLeft: "4px",
});

const EditedBadge = styled(Chip)(({ theme }) => ({
  background: "rgba(2, 136, 209, 0.08)",
  color: "#42a5f5",
  fontSize: "11px",
  height: "20px",
  fontWeight: 500,
  border: "none",
  "& .MuiChip-label": {
    padding: "1 5px",
  },
  borderRadius: "100px",
}));

type BOMNavigationProps = {
  condition: ItemWithBOM | null;
  activeItem: ItemWithBOM | null;
  onItemClick: (item: ItemWithBOM) => void;
  activePathOverride?: string;
  diffPaths?: Set<string>;
};

type ChangeSummary = {
  changedItemPaths: Set<string>; // Position-based paths for edited items
  changedProcesses: Set<string>;
  newItemPaths: Set<string>; // Position-based paths instead of identifiers
  newProcessPaths: Set<string>; // Position-based paths instead of identifiers
};

type BOMTreeNodeProps = {
  item: ItemWithBOM;
  depth: number;
  searchQuery: string;
  activeItem: ItemWithBOM | null;
  onItemClick: (item: ItemWithBOM) => void;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  changeSummary: ChangeSummary;
  itemPath: string; // URL-style path for this item
  activePathOverride?: string;
  diffPaths?: Set<string>;
  ancestorPendingDeletion?: boolean;
  parentItem?: ItemWithBOM; // Parent item to check for deletion status
  bomIndex?: number; // Index in parent's BOM array
};

const BOMTreeNode = ({
  item,
  depth,
  searchQuery,
  activeItem,
  onItemClick,
  searchParams,
  setSearchParams,

  changeSummary,
  itemPath,
  activePathOverride,
  diffPaths,
  ancestorPendingDeletion = false,
  parentItem,
  bomIndex,
}: BOMTreeNodeProps) => {
  const itemData = item.item;
  const bom = item.bom || [];

  // Subscribe to violation changes to trigger re-renders when violations update
  const violationSummary = useViolationSummary();

  // Check if this item has any constraint violations using path-based lookup
  const itemViolations = violationSummary.getItemViolationsByPath(itemPath);
  const hasViolations = itemViolations.length > 0;

  // Check if this item has any changes (attributes, parameters, additions, deletions)
  const hasChanges = changeSummary.changedItemPaths.has(itemPath);

  // Check if this item is newly added using position-based path
  const isNew = changeSummary.newItemPaths.has(itemPath);

  // Check if this item is pending deletion by looking at parent's bom_changes
  const isPendingDeletion =
    parentItem && bomIndex !== undefined
      ? parentItem.bom_changes?.some(
          (c) => c.index === bomIndex && c.is_deleted && !c.is_added
        ) || false
      : false;

  // Check if this item has any new processes using position-based paths
  const hasNewProcesses =
    item.processes?.some((proc, index) => {
      const processPath = itemPath ? `${itemPath}:p${index}` : `p${index}`;
      return changeSummary.newProcessPaths.has(processPath);
    }) || false;

  const matchesSearch = (text: string) => {
    return text.toLowerCase().includes(searchQuery.toLowerCase());
  };

  // Check if this item or its immediate BOM items match the search
  const matchesItemOrChildren = (currentItem: ItemWithBOM): boolean => {
    // Check current item
    if (
      matchesSearch(currentItem.item.item_type_name) ||
      matchesSearch(currentItem.item.name) ||
      matchesSearch(currentItem.item.arena_part_number)
    ) {
      return true;
    }

    // Check current item's attributes
    const attributeKeys = Object.keys(currentItem.item.attributes);
    if (attributeKeys.some((key) => matchesSearch(key))) {
      return true;
    }

    // Check only immediate BOM items (one level deep)
    if (currentItem.bom && currentItem.bom.length > 0) {
      return currentItem.bom.some((bomItem) => {
        // Check BOM item properties
        if (
          matchesSearch(bomItem.item.item_type_name) ||
          matchesSearch(bomItem.item.name) ||
          matchesSearch(bomItem.item.arena_part_number)
        ) {
          return true;
        }

        // Check BOM item attributes
        const bomAttributeKeys = Object.keys(bomItem.item.attributes);
        return bomAttributeKeys.some((key) => matchesSearch(key));
      });
    }

    return false;
  };

  const shouldShow = searchQuery === "" || matchesItemOrChildren(item);

  if (!shouldShow) {
    return null;
  }

  // Use path comparison instead of object reference to survive state updates
  const activeItemPath =
    activePathOverride !== undefined
      ? activePathOverride
      : searchParams.get("bom-item") || "";
  const isActive = itemPath === activeItemPath;
  const hasDiffs = Boolean(diffPaths?.has(itemPath));

  const hasChildren = bom.length > 0;
  const isDisabled = isPendingDeletion || ancestorPendingDeletion;

  return (
    <>
      <Tooltip
        title={
          isDisabled
            ? "This item is pending deletion"
            : "description" in itemData && itemData.description
            ? itemData.description
            : ""
        }
        placement="right"
        arrow
        enterDelay={500}
      >
        <TreeRow
          depth={depth}
          isActive={isActive}
          disabled={isDisabled}
          onClick={() => !isDisabled && onItemClick(item)}
        >
          <TreeLabel>
            {hasViolations && (
              <Warning
                sx={{
                  fontSize: "16px",
                  color: "#ff9800",
                  cursor: "pointer",
                }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "space-between",
                }}
              >
                <ItemTypeLabel>{itemData.item_type_name}</ItemTypeLabel>
                <Box sx={{ display: "flex", gap: "4px" }}>
                  {isNew && <StatusBadge label="New" size="small" />}
                  {!isNew && (hasChanges || hasNewProcesses) && (
                    <EditedBadge label="Edited" size="small" />
                  )}
                  {hasDiffs && !isNew && (
                    <EditedBadge label="Diff" size="small" />
                  )}
                </Box>
              </Box>
              <ItemNameLabel>{itemData.name}</ItemNameLabel>
            </Box>
          </TreeLabel>
        </TreeRow>
      </Tooltip>

      {hasChildren && (
        <DepthLineContainer depth={depth + 1}>
          {depth >= 0 && <DepthLine depth={depth + 1} />}
          {bom.map((bomItem, bomIndex) => {
            // Build child's URL path using absolute BOM index
            const childPath = itemPath
              ? `${itemPath}>${bomIndex}`
              : `${bomIndex}`;

            return (
              <BOMTreeNode
                key={`${bomItem.item.arena_part_number}-${depth}-${bomIndex}`}
                item={bomItem}
                depth={depth + 1}
                searchQuery={searchQuery}
                activeItem={activeItem}
                onItemClick={onItemClick}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                changeSummary={changeSummary}
                itemPath={childPath}
                activePathOverride={activePathOverride}
                diffPaths={diffPaths}
                ancestorPendingDeletion={isDisabled}
                parentItem={item}
                bomIndex={bomIndex}
              />
            );
          })}
        </DepthLineContainer>
      )}
    </>
  );
};

const BOMNavigation = ({
  condition,
  activeItem,
  onItemClick,
  activePathOverride,
  diffPaths,
}: BOMNavigationProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const changeSummary = useMemo<ChangeSummary>(() => {
    const changedItemPaths = new Set<string>();
    const changedProcesses = new Set<string>();
    const newItemPaths = new Set<string>();
    const newProcessPaths = new Set<string>();

    // Helper function to recursively collect changes from the BOM tree
    // Returns true if this item or any of its descendants have changes
    const collectChanges = (
      item: ItemWithBOM,
      urlPath: string, // URL-style path for position tracking
      parentBomIndex?: number,
      parentIsNew: boolean = false
    ): boolean => {
      // Check if this item itself is newly added (check parent's bom_changes)
      // For root item, it's never considered "new"
      let hasChanges = false;

      // A replaced item carries no change arrays of its own.
      if (item.item_replaced) {
        changedItemPaths.add(urlPath);
        hasChanges = true;
      }

      // Check if this item has any attribute changes
      if (item.item_changes && item.item_changes.length > 0) {
        changedItemPaths.add(urlPath);
        hasChanges = true;
      }

      // Check if this item has any BOM changes
      if (item.bom_changes && item.bom_changes.length > 0) {
        changedItemPaths.add(urlPath);
        hasChanges = true;
      }

      // Check if this item has any process changes (additions/deletions)
      if (item.process_changes && item.process_changes.length > 0) {
        // Check if any processes are newly added
        const newProcessChanges = item.process_changes.filter(
          (c) => c.is_added && !c.is_deleted
        );
        if (newProcessChanges.length > 0) {
          newProcessChanges.forEach((change) => {
            const processPath = urlPath
              ? `${urlPath}:p${change.index}`
              : `p${change.index}`;
            newProcessPaths.add(processPath);
          });
        }

        // Add to changed items if there are any process changes
        changedItemPaths.add(urlPath);
        hasChanges = true;
      }

      // Check if any processes (or their nested steps) have parameter changes
      if (item.processes && item.processes.length > 0) {
        const hasParameterChanges = item.processes.some(processTreeHasChanges);
        if (hasParameterChanges) {
          changedItemPaths.add(urlPath);
          hasChanges = true;
        }
      }

      // If parent is new, mark all processes as new using position-based paths
      if (parentIsNew && item.processes) {
        item.processes.forEach((proc, index) => {
          const processPath = urlPath ? `${urlPath}:p${index}` : `p${index}`;
          newProcessPaths.add(processPath);
        });
      }

      // Recursively check children
      if (item.bom && item.bom.length > 0) {
        item.bom.forEach((bomItem, index) => {
          // Build URL-style path segment using absolute BOM index
          const childUrlPath = urlPath ? `${urlPath}>${index}` : `${index}`;

          // Check if this BOM item is newly added
          const isNewBomItem = item.bom_changes?.some(
            (c) => c.index === index && c.is_added && !c.is_deleted
          );

          // Mark as new if this item is new OR if parent was new
          if (isNewBomItem || parentIsNew) {
            newItemPaths.add(childUrlPath);
          }

          // Pass down the "new" status to children and check if child has changes
          const childHasChanges = collectChanges(
            bomItem,
            childUrlPath,
            index,
            isNewBomItem || parentIsNew
          );

          // If child has changes, propagate that up to this parent
          if (childHasChanges) {
            changedItemPaths.add(urlPath);
            hasChanges = true;
          }
        });
      }

      return hasChanges;
    };

    if (condition) {
      collectChanges(condition, "");
    }

    return {
      changedItemPaths,
      changedProcesses,
      newItemPaths,
      newProcessPaths,
    };
  }, [condition]);

  const activePath =
    activePathOverride !== undefined
      ? activePathOverride
      : searchParams.get("bom-item") || "";

  return (
    <Panel elevation={0}>
      <PanelHeader>
        <SearchField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search BOM, attribute, or process..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </PanelHeader>
      <ScrollableContent>
        {condition !== null ? (
          <BOMTreeNode
            item={condition}
            depth={0}
            searchQuery={searchQuery}
            activeItem={activeItem}
            onItemClick={onItemClick}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            changeSummary={changeSummary}
            itemPath=""
            activePathOverride={activePath}
            diffPaths={diffPaths}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            No condition selected
          </Typography>
        )}
      </ScrollableContent>
    </Panel>
  );
};

export default BOMNavigation;
